import React from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get } from 'lodash';
import { Box, Paper, Typography, Button, Divider } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Dialog, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useSelector } from 'react-redux';
import messages from 'containers/LawyerFeeNote/messages';
import { makeSelectKpiOverDueInvoice } from 'containers/LawyerFeeNote/selectors';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import { translate } from 'containers/services/translate';
import { Listings } from 'containers/AppContainer/typings';
import { getLimitInvoiceManager } from 'services/utils';

interface CategoryItem {
  label: string;
  count: number;
  status: string;
}

interface OverdueInvoicesCardProps {
  open: boolean;
  intl: IntlShape;
  listings: Listings;
  onClose: () => void;
}

const useStyles = makeStyles({
  root: {},

  container: {
    width: '100%',
    maxWidth: '1024px',
    borderRadius: '18px',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.10)',
  },

  header: {
    backgroundColor: '#0d7fd3',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    padding: '40px 48px',
    boxSizing: 'border-box',
    flexWrap: 'wrap',
  },

  closeButton: {
    backgroundColor: 'rgba(255, 255, 255, 0.18)',
    color: 'white',
    width: '40px',
    height: '40px',
  },

  iconCircle: {
    width: '84px',
    height: '84px',
    minWidth: '84px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.18)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: '24px',
  },

  iconInner: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    border: '3px solid #ffffff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: 1,
    color: '#ffffff',
  },

  headerText: {
    display: 'flex',
    flexDirection: 'column',
  },
  headerIcon: {
    display: 'flex',
    position: 'absolute',
    alignSelf: 'center',
    right: '47px',
  },

  headerTitle: {
    fontSize: '22px',
    fontWeight: 500,
    marginBottom: '8px',
    color: '#ffffff',
  },

  statsRow: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
  },

  bigNumber: {
    fontSize: '58px',
    fontWeight: 700,
    lineHeight: 1,
    marginRight: '12px',
    color: '#ffffff',
  },

  statsText: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#ffffff',
  },

  body: {
    padding: '40px 48px 44px',
    boxSizing: 'border-box',
  },

  sectionTitle: {
    fontSize: '20px',
    fontWeight: 700,
    textTransform: 'uppercase',
    color: '#4b4b4b',
    marginBottom: '24px',
    letterSpacing: '0.4px',
  },

  cardsRow: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  },

  cardWrapper: {
    flex: '1 1 260px',
    minWidth: '260px',
  },

  categoryCard: {
    backgroundColor: '#f4f2ed',
    borderRadius: '16px',
    minHeight: '190px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '24px 16px',
    boxSizing: 'border-box',
  },

  categoryTitle: {
    fontSize: '18px',
    fontWeight: 500,
    color: '#444444',
    marginBottom: '12px',
  },

  categoryCount: {
    fontSize: '54px',
    fontWeight: 700,
    color: '#0d7fd3',
    lineHeight: 1.1,
    marginBottom: '16px',
  },

  statusChip: {
    backgroundColor: '#dfeeff',
    color: '#1f5f9d',
    borderRadius: '999px',
    padding: '8px 18px',
    fontSize: '16px',
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },

  divider: {
    margin: '40px 0 24px',
    backgroundColor: '#e5e5e5',
  },

  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '28px',
    flexWrap: 'wrap',
  },

  totalLabel: {
    fontSize: '20px',
    fontWeight: 500,
    color: '#444444',
  },

  totalValue: {
    fontSize: '22px',
    fontWeight: 700,
    color: '#222222',
  },

  actions: {
    display: 'flex',
    gap: '20px',
    flexWrap: 'wrap',
  },

  primaryButton: {
    flex: 1,
    minWidth: '240px',
    height: '68px',
    borderRadius: '14px',
    backgroundColor: '#0d7fd3',
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: 'none',
  },

  secondaryButton: {
    flex: 1,
    minWidth: '240px',
    height: '68px',
    borderRadius: '14px',
    backgroundColor: '#ffffff',
    color: '#444444',
    border: '1px solid #e0e0e0',
    fontSize: '18px',
    fontWeight: 500,
    textTransform: 'none',
  },
});

const OverdueInvoicesCard: React.FC<OverdueInvoicesCardProps> = props => {
  const classes = useStyles();
  const kpiOverDueInvoice = useSelector(makeSelectKpiOverDueInvoice());
  const data: CategoryItem[] = featureService.hasFeature(FEATURES.TRANSACTION)
    ? [
        {
          label: translate(messages.opening, props),
          count: kpiOverDueInvoice.opening || 0,
          status: translate(messages.kpiInvoiceDialogToTrait, props),
        },
        {
          label: translate(messages.judiciary, props),
          count: kpiOverDueInvoice.judiciary || 0,
          status: translate(messages.kpiInvoiceDialogToTrait, props),
        },
        {
          label: translate(messages.transaction, props),
          count: kpiOverDueInvoice.transaction || 0,
          status: translate(messages.kpiInvoiceDialogToTrait, props),
        },
      ]
    : [
        {
          label: translate(messages.opening, props),
          count: kpiOverDueInvoice.opening || 0,
          status: translate(messages.kpiInvoiceDialogToTrait, props),
        },
        {
          label: translate(messages.judiciary, props),
          count: kpiOverDueInvoice.judiciary || 0,
          status: translate(messages.kpiInvoiceDialogToTrait, props),
        },
      ];

  const total = data.reduce((sum, item) => sum + item.count, 0);

  return (
    <Dialog
      open={props.open}
      onClose={props.onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        style: {
          borderRadius: '18px',
          overflow: 'hidden',
          backgroundColor: 'transparent',
          boxShadow: 'none',
          width: 'unset',
        },
      }}
    >
      <Box>
        <Box>
          <Paper elevation={0} className={classes.container}>
            <Box className={classes.header}>
              <Box className={classes.iconCircle}>
                <Box className={classes.iconInner}>!</Box>
              </Box>

              <Box className={classes.headerText}>
                <Typography className={classes.headerTitle}>
                  {translate(messages.kpiInvoiceDialogTitle, props)}
                </Typography>

                <Box className={classes.statsRow}>
                  <Typography component="span" className={classes.bigNumber}>
                    {total}
                  </Typography>

                  <Typography component="span" className={classes.statsText}>
                    {translate(messages.kpiInvoiceDialogInvoiceTitle, props, {
                      limitDaysManager: getLimitInvoiceManager(
                        get(props.listings, 'appConfig', []),
                      ),
                    })}
                  </Typography>
                </Box>
              </Box>
              <Box className={classes.headerIcon}>
                <IconButton
                  className={classes.closeButton}
                  onClick={props.onClose}
                  aria-label="Fermer"
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>

            <Box className={classes.body}>
              <Typography className={classes.sectionTitle}>
                {translate(messages.kpiInvoiceDialogSubTitle, props, {
                  limitDaysManager: getLimitInvoiceManager(
                    get(props.listings, 'appConfig', []),
                  ),
                })}
              </Typography>

              <Box className={classes.cardsRow}>
                {data.map(item => (
                  <Box key={item.label} className={classes.cardWrapper}>
                    <Box className={classes.categoryCard}>
                      <Typography className={classes.categoryTitle}>
                        {item.label}
                      </Typography>

                      <Typography className={classes.categoryCount}>
                        {item.count}
                      </Typography>

                      <Box className={classes.statusChip}>{item.status}</Box>
                    </Box>
                  </Box>
                ))}
              </Box>

              <Divider className={classes.divider} />

              <Box className={classes.totalRow}>
                <Typography className={classes.totalLabel}>
                  {translate(messages.kpiInvoiceDialogTotal, props)}
                </Typography>

                <Typography className={classes.totalValue}>
                  {translate(
                    messages.kpiInvoiceDialogInvoiceFooterTitle,
                    props,
                    {
                      total: total,
                    },
                  )}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Dialog>
  );
};

export default injectIntl(OverdueInvoicesCard);
